"use client";
/**
 * Per-profile layout namespacing.
 *
 * Display Profiles used to work by COPYING every layout key into a profile's
 * snapshot and copying it back on switch. That copy drifts: press the page's
 * Save without also "Update"-ing the profile and the stored copy no longer
 * matches the screen; a pane added after a profile was saved bleeds across
 * because both profiles write the same physical keys.
 *
 * This module removes the copy. When a profile is active, every PER-MACHINE
 * layout key (pane sizes, split dividers, which library layout a pane shows,
 * grid arrangements — see {@link isPerMachineLayoutKey}) is transparently
 * stored under a physical slot suffixed with the profile id. Reads and writes
 * of the LOGICAL key redirect to that slot, so:
 *   - The layout you edit while a profile is loaded IS that profile's layout —
 *     the page's Save is the only save; there is nothing to keep in sync.
 *   - Switching profiles just points storage at a different slot. No copy, no
 *     drift, no bleed — what a profile reads back is always its own slot.
 *   - Reads are an OVERLAY: a profile only overrides the keys it has actually
 *     set; anything its namespace lacks falls back to the shared, un-namespaced
 *     baseline. So an empty or partial profile renders as the base layout
 *     instead of a blank page, and customizations layer on top per profile.
 *   - Writes are WRITE-THROUGH: the slot, plus the baseline. A profile that has
 *     never saved a given key follows your most recent arrangement rather than a
 *     stale pre-profiles one; the moment it saves that key it has its own slot
 *     and stops following. Without this, a page saved under one profile simply
 *     vanished when you switched to another — the bug that prompted it.
 *
 * It works by patching localStorage.get/set/removeItem at the BOTTOM of the
 * stack (installed before lib/prefsSync and lib/workspace/saveGate patch over
 * it), so every layer above — including react-resizable-panels' own autosave —
 * transparently lands in the active profile's slot. Non-layout keys and the
 * no-active-profile case pass straight through unchanged.
 */
import { isPerMachineLayoutKey } from "@/lib/safeLocalStorage";

/** Separator for physical slots. NUL guarantees it never collides with a real
 *  key (localStorage keys the app writes never contain NUL). */
const SEP = "\u0000@dp:";
/** Per-machine (non-synced, non-`mpp:`) one-time migration marker. Bumped to v2
 *  because an early build set v1 pre-hydrate with zero profiles present, leaving
 *  poisoned stores that then skipped the corrected migration. */
const MIGRATED_FLAG = "displayProfileNsMigrated:v2";

let installed = false;
let activeId: string | null = null;
// The ORIGINAL localStorage methods, captured before any patch wraps them.
let origGet!: (k: string) => string | null;
let origSet!: (k: string, v: string) => void;
let origRemove!: (k: string) => void;

function isPhysical(k: string): boolean {
  return k.indexOf(SEP) !== -1;
}

/**
 * Layout keys that must NOT write through to the shared baseline.
 *
 * Write-through exists because most baselines are stale relics from before
 * profiles existed, so inheriting one shows a months-old layout. A workspace
 * TREE override is the opposite: what it falls back to is the live shared
 * library copy (`mpp:workspaces:v1`), which is perfectly current. Mirroring it
 * would push the screen you just edited onto every screen that hasn't diverged
 * yet — exactly what the override exists to prevent ("I added a pane on the
 * 49-inch and it turned up on the laptop").
 */
function isProfilePrivate(k: string): boolean {
  return k.startsWith("mpp:ws-tree:");
}
function physical(logical: string, id: string): string {
  return logical + SEP + id;
}

/** Logical key -> physical slot for the active profile (identity when no
 *  profile is active, the key is already physical, or it isn't layout state). */
function remap(k: string): string {
  if (isPhysical(k)) return k; // already a slot — never double-namespace
  if (!activeId) return k;
  if (!isPerMachineLayoutKey(k)) return k;
  return physical(k, activeId);
}

/** Patch localStorage so layout keys redirect to the active profile's slot.
 *  Idempotent; must be installed before prefsSync / saveGate patch setItem. */
export function installLayoutNamespace(): void {
  if (installed || typeof window === "undefined") return;
  const ls = window.localStorage;
  origGet = ls.getItem.bind(ls);
  origSet = ls.setItem.bind(ls);
  origRemove = ls.removeItem.bind(ls);
  installed = true;
  // Reads are an OVERLAY: a profile only overrides keys it has actually set;
  // any layout key its namespace lacks falls back to the shared, un-namespaced
  // baseline. So an empty / partial profile renders identically to the base
  // layout instead of shadowing it with nothing (which showed a blank page).
  ls.getItem = (k: string) => {
    const key = String(k);
    if (activeId && !isPhysical(key) && isPerMachineLayoutKey(key)) {
      const hit = origGet(physical(key, activeId));
      return hit !== null ? hit : origGet(key);
    }
    return origGet(key);
  };
  // Writes land in the active profile's own slot — and ALSO refresh the shared
  // baseline. The slot is what the active profile reads back, so profiles still
  // can't bleed into one another; the baseline is only what a profile falls back
  // to for keys it has never set itself. Keeping it current is what stops the
  // "I saved the page, switched profile, my change is gone" report: an
  // un-customized profile now inherits your LATEST arrangement instead of
  // whatever the shared layout happened to hold before profiles existed. The
  // first save under a profile writes its slot, and from then on that profile
  // keeps its own version of that key. Clears must mirror this (remove slot +
  // baseline) or getItem's overlay resurrects the baseline after restore.
  ls.setItem = (k: string, v: string) => {
    const key = String(k);
    const val = String(v);
    const slot = remap(key);
    origSet(slot, val); // authoritative for the active profile
    if (slot !== key && !isProfilePrivate(key)) {
      try {
        origSet(key, val);
      } catch {
        /* quota — the slot write above is the one that matters */
      }
    }
  };
  // Mirror setItem's write-through on clear. getItem overlays the profile slot
  // over the shared baseline — if we only remove the slot, restore falls back
  // to a stale baseline and Market Breadth / workspace maximize comes back
  // every visit even after the user restored.
  ls.removeItem = (k: string) => {
    const key = String(k);
    const slot = remap(key);
    origRemove(slot);
    if (slot !== key && !isProfilePrivate(key)) {
      try {
        origRemove(key);
      } catch {
        /* ignore */
      }
    }
  };
}

/** Point layout reads/writes at profile `id` (null = the shared, un-namespaced
 *  layout). Cheap — just swaps the cached id the remap consults. */
export function setActiveNamespace(id: string | null): void {
  activeId = id && id.length ? id : null;
}

/** Write a key EXACTLY as given, bypassing the namespace remap — so config
 *  restore can place captured physical slots / per-machine layout keys verbatim
 *  instead of having them re-routed into the active profile's slot. */
export function rawSet(key: string, value: string): void {
  if (typeof window === "undefined") return;
  if (installed) origSet(key, value);
  else window.localStorage.setItem(key, value);
}

/** Read a key EXACTLY as given — the un-namespaced baseline, not the active
 *  profile overlay. Used when seeding a clone so we can fall back to the
 *  shared page grid without picking up whichever profile happens to be loaded. */
export function rawGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  if (installed) return origGet(key);
  return window.localStorage.getItem(key);
}

/** Every layout key stored un-namespaced (the shared / no-profile layout). */
function liveLogicalLayoutKeys(): string[] {
  const out: string[] = [];
  const ls = window.localStorage;
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i);
    if (k && !isPhysical(k) && isPerMachineLayoutKey(k)) out.push(k);
  }
  return out;
}

/** Read one logical key from a SPECIFIC profile's namespace (not the active
 *  one) — used to label profiles other than the one currently loaded. */
export function readNamespaced(id: string, logicalKey: string): string | null {
  if (typeof window === "undefined" || !installed) return null;
  return origGet(physical(logicalKey, id));
}

/** Write one logical key into a SPECIFIC profile's namespace without
 *  write-through to the shared baseline — used when seeding a clone so the
 *  laptop arrangement cannot leak onto the ultrawide. */
export function writeNamespaced(id: string, logicalKey: string, value: string): void {
  if (typeof window === "undefined" || !installed) return;
  origSet(physical(logicalKey, id), value);
}

/** The logical layout keys a profile's namespace currently holds. */
export function namespaceLogicalKeys(id: string): string[] {
  const out: string[] = [];
  const ls = window.localStorage;
  const suffix = SEP + id;
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i);
    if (k && k.endsWith(suffix) && k.length > suffix.length) out.push(k.slice(0, -suffix.length));
  }
  return out;
}

/** Copy the current effective layout into `dstId`'s namespace, so a new profile
 *  starts as a clone of what's on screen. `srcId` null = copy the shared layout. */
export function seedNamespaceFrom(srcId: string | null, dstId: string): void {
  if (typeof window === "undefined" || !installed) return;
  if (srcId) {
    for (const logical of namespaceLogicalKeys(srcId)) {
      const v = origGet(physical(logical, srcId));
      if (v != null) origSet(physical(logical, dstId), v);
    }
  } else {
    for (const logical of liveLogicalLayoutKeys()) {
      const v = origGet(logical);
      if (v != null) origSet(physical(logical, dstId), v);
    }
  }
}

/** Delete every physical slot belonging to a profile. */
export function purgeNamespace(id: string): void {
  if (typeof window === "undefined" || !installed) return;
  const suffix = SEP + id;
  const doomed: string[] = [];
  const ls = window.localStorage;
  for (let i = 0; i < ls.length; i++) {
    const k = ls.key(i);
    if (k && k.endsWith(suffix)) doomed.push(k);
  }
  for (const k of doomed) origRemove(k);
}

/**
 * One-time upgrade from the old copy model. Seeds each existing profile's
 * namespace from its legacy snapshot (which only ever held layout SELECTIONS,
 * never sizes). It does NOT copy the live layout into any namespace — with the
 * overlay read model, a profile inherits the shared baseline for every key it
 * doesn't set, so copying would only duplicate storage (and risk the quota) for
 * no behavioral gain.
 *
 * Bulletproof by construction: every write is quota-guarded, and the whole
 * thing is wrapped by callers — profile setup must NEVER throw out to the render
 * gate, or the app renders blank. A partial or skipped migration is harmless:
 * the overlay falls back to the shared layout.
 */
export function migrateLayoutNamespaces(
  profiles: { id: string; snapshot?: Record<string, string> }[],
  _activeProfileId?: string | null,
): void {
  if (typeof window === "undefined" || !installed) return;
  try { if (origGet(MIGRATED_FLAG) === "1") return; } catch { return; }
  // Nothing to migrate yet (e.g. a hydrate timeout left the synced profile
  // definitions absent) — do NOT flag; retry on a later load once they arrive.
  if (profiles.length === 0) return;
  for (const p of profiles) {
    if (!p.snapshot) continue;
    for (const [k, v] of Object.entries(p.snapshot)) {
      if (typeof v === "string" && isPerMachineLayoutKey(k)) {
        try { origSet(physical(k, p.id), v); } catch { /* quota — overlay covers it */ }
      }
    }
  }
  try { origSet(MIGRATED_FLAG, "1"); } catch { /* retry next load; overlay renders meanwhile */ }
}
